// packages/components/src/doctor-ai/MedicalSummaryTab.tsx
import React from 'react';
import type { Patient, MedicalItem } from '@dopomogai/types';
import { Badge } from '@dopomogai/ui';
import { HeartPulse, Pill, ListChecks, Users, Cigarette, Stethoscope, Phone } from 'lucide-react';

// A reusable section component for consistency
const SummarySection: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="bg-white border-2 border-black rounded-xl p-5">
    <h4 className="flex items-center gap-2 text-md font-bold text-slate-800 mb-4">
      {icon} {title}
    </h4>
    {children}
  </div>
);

// A component to render lists of medical items
const MedicalItemList: React.FC<{ items: MedicalItem[], noItemText?: string }> = ({ items, noItemText = "None Reported" }) => {
  if (items.length === 0) {
    return <p className="text-sm text-slate-500">{noItemText}</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="text-sm">
          <strong className="text-slate-700">{item.name}</strong>
          {item.notes && <p className="text-slate-500 text-xs">- {item.notes}</p>}
        </li>
      ))}
    </ul>
  );
};


export const MedicalSummaryTab: React.FC<{ patient: Patient }> = ({ patient }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SummarySection title="Chronic Conditions" icon={<HeartPulse size={18} />}>
            <MedicalItemList items={patient.chronicConditions} />
        </SummarySection>
        <SummarySection title="Current Medications" icon={<Pill size={18} />}>
            <MedicalItemList items={patient.currentMedications} />
        </SummarySection>
        <SummarySection title="Surgical History" icon={<ListChecks size={18} />}>
            <MedicalItemList items={patient.surgicalHistory} />
        </SummarySection>
        <SummarySection title="Family History" icon={<Users size={18} />}>
            <MedicalItemList items={patient.familyHistory} />
        </SummarySection>
      </div>

       <SummarySection title="Social & Care Team" icon={<Stethoscope size={18} />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Smoking Status</p>
                    <Badge text={patient.smokingStatus} accentColor={patient.smokingStatus === 'Current smoker' ? 'yellow' : 'blue'} className="mt-1" />
                </div>
                <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Primary Physician</p>
                    <p className="font-bold text-slate-700">{patient.primaryCarePhysician}</p>
                </div>
                 <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase">Emergency Contact</p>
                    <p className="font-bold text-slate-700">{patient.emergencyContact.name} <span className="text-slate-500 font-normal">({patient.emergencyContact.relation})</span></p>
                    <a href={`tel:${patient.emergencyContact.phone}`} className="text-indigo-600 hover:underline flex items-center gap-1">
                        <Phone size={12} /> {patient.emergencyContact.phone}
                    </a>
                </div>
            </div>
       </SummarySection>
    </div>
  );
};